/**
 * Journal — find and fix anything.
 *
 * Totals here are summed from SPLITS, never from entry totals (AGENTS.md §3.2),
 * and each split arrives with its bucket already resolved by the API, so a
 * per-line bucket override is respected without the client re-implementing the
 * rule.
 *
 * Every figure above the list — the headline, and the in/out line under it —
 * comes from the MONTH's budget summary, never from the entries on screen, and
 * nothing that narrows the list may move them (AGENTS.md §3.5e). They were
 * once summed from the loaded entries, which made them change every time the
 * filter was touched, and change to something meaningless: filtering by Needs
 * returns whole entries that merely CONTAIN a Needs split, wants-half and all.
 * Reading the summary the Budget screen reads also means the two cannot
 * disagree.
 *
 * Per-bucket totals are the Budget screen's job and are not repeated here.
 *
 * `EntryFilters` — the search box, the applied-filter chips and the facet
 * popover — is exported from here and rendered by App inside the page header,
 * because the header is the one band that is already sticky: a filter that
 * scrolls away is a filter you scroll back up to reach. It lives in this file
 * rather than its own because it is the other half of one feature: the two
 * share `JournalFilters`, and `q` is passed down so the box and the matching
 * cannot drift apart.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type {
  Entry,
  EntryList,
  Household,
  Member,
  PaymentMeans,
  PeriodSummary,
} from '@et/contracts';
import {
  add,
  BUCKET_LABELS,
  SPENDING_BUCKETS,
  formatMoney,
  parseMoney,
  zero,
  type SpendingBucket,
  type Money,
} from '@et/core';
import { dayLabel } from './months.js';

export interface JournalFilters {
  month: string; // YYYY-MM
  spentBy: string;
  bucket: string;
  paymentMeansId: string;
}

interface Props {
  household: Household;
  data: EntryList | null;
  /** The month's totals. Deliberately not derived from `data` — see the note above. */
  summary: PeriodSummary | null;
  /** Read, not written: the controls that set these live in the header. */
  filters: JournalFilters;
  /** The header's search box. Not part of `filters` — see EntryFilters. */
  q: string;
  /**
   * Called when the summary block scrolls behind the header, so the header can
   * show the figure instead. Pass a `useState` setter — a fresh closure every
   * render would rebuild the listener every render.
   */
  onSummaryHidden: (hidden: boolean) => void;
  onEdit: (entry: Entry) => void;
  onDelete: (entry: Entry) => void;
  loading: boolean;
}


/** How many line items an entry carries, across all of its splits. */
function itemCount(entry: Entry): number {
  return entry.splits.reduce((total, split) => total + split.items.length, 0);
}

/**
 * What to call this entry in one line.
 *
 * For spending, the category is a useful fallback: "Home & decor" says
 * something. For income it is not — every income entry files under the single
 * "Income" category, so falling back to it would label every payday the same
 * word. The source IS the classification for income (AGENTS.md §3.5d), so the
 * source names are the headline.
 */
function headline(entry: Entry): string {
  // Spending says WHERE, income says WHAT CAME IN. Both are now required at
  // the point of entry, so the fallbacks below are for entries recorded before
  // those rules.
  if (entry.merchantName) return entry.merchantName;

  const names = [
    ...new Set(
      entry.splits.flatMap((split) =>
        split.items.map((item) => item.productName ?? item.rawText).filter((n): n is string => !!n),
      ),
    ),
  ];
  if (names.length > 0) return names.join(', ');

  // Deliberately NOT the category. This line is where the merchant goes, and
  // printing "Supermarket" here says the money went to a shop called
  // Supermarket — the same collision the item-naming rule exists to prevent
  // (AGENTS.md §3.5b(ii)).
  return entry.note ?? 'Entry';
}

export function Journal({
  household,
  data,
  summary,
  filters,
  q,
  onSummaryHidden,
  onEdit,
  onDelete,
  loading,
}: Props) {
  const base = household.baseCurrency;
  const entries = data?.entries ?? [];

  const grandTotal = useMemo(
    () => (summary ? parseMoney(summary.totalActual, base) : null),
    [summary, base],
  );

  /**
   * What is left: income minus everything spent, which the API already
   * computes as `unallocated`. Recomputing it here from the entry list would
   * give a different answer the moment a filter is on — the same bug the
   * bucket tiles had.
   */
  const overspent = isOverspent(summary);
  const figure = remainingFigure(summary, base);

  /**
   * Search lives here and not in `filters`, because it never reaches the API:
   * the month is already loaded, so matching happens in the browser and every
   * keystroke is instant. Putting it in `filters` would refetch the month on
   * each letter typed.
   */
  /**
   * Held in state via a callback ref rather than `useRef` + `useEffect([])`:
   * that pattern has already cost this codebase a silently dead effect
   * (AGENTS.md §8), and a node in state cannot be missed.
   */
  const [summaryEl, setSummaryEl] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!summaryEl) return;
    function headHeight(): number {
      const raw = getComputedStyle(document.documentElement).getPropertyValue('--head-h');
      const value = Number.parseFloat(raw);
      return Number.isFinite(value) ? value : 96;
    }
    function check() {
      // Against the header's bottom, not the viewport's top: the header is
      // sticky, so a block "on screen" can still be entirely behind it.
      onSummaryHidden(summaryEl!.getBoundingClientRect().bottom <= headHeight());
    }
    check();
    window.addEventListener('scroll', check, { passive: true });
    window.addEventListener('resize', check);
    return () => {
      window.removeEventListener('scroll', check);
      window.removeEventListener('resize', check);
    };
  }, [summaryEl, onSummaryHidden]);

  const found = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return entries;
    return entries.filter((entry) => haystack(entry).includes(needle));
  }, [entries, q]);

  const byDay = useMemo(() => {
    const groups = new Map<string, Entry[]>();
    for (const entry of found) {
      const list = groups.get(entry.spentAt);
      if (list) list.push(entry);
      else groups.set(entry.spentAt, [entry]);
    }
    return [...groups.entries()].sort((a, b) => b[0].localeCompare(a[0]));
  }, [found]);

  return (
    <section className="journal">

      {/* "Spent" is a fact about the past; what is left is the number you act
          on, so it is the large one. The search and the facets are not here:
          they live in the page header, which is the band that is already
          sticky (AGENTS.md §3.5e). */}
      <div
        ref={setSummaryEl}
        className={overspent ? 'remaining remaining-over' : 'remaining'}
      >
        <span className="remaining-label">
          {overspent ? 'Overspent this month' : 'Left this month'}
        </span>
        <strong className="remaining-figure">{figure}</strong>
        <span className="remaining-parts">
          {summary ? (
            <>
              <span className="amount-in">+{summary.netIncome}</span> in ·{' '}
              <span>&minus;{grandTotal ? formatMoney(grandTotal) : '0.00'}</span> out
            </>
          ) : (
            'Loading…'
          )}
        </span>
      </div>

      {loading && <p className="hint">Loading…</p>}

      {/* `found`, not `entries`: a search that matches nothing leaves the
          list empty while the server still returned rows, and an empty page
          with no sentence on it reads as a bug. */}
      {!loading && found.length === 0 && (
        <p className="hint">
          {q.trim()
            ? `Nothing in this month matches “${q.trim()}”.`
            : 'Nothing recorded for these filters yet.'}
        </p>
      )}

      {data?.hasMore && (
        <p className="hint">
          Showing the first {entries.length} entries — narrow the filters to see the rest.
        </p>
      )}

      {byDay.map(([day, dayEntries]) => (
        <div className="day" key={day}>
          <h3>
            <span>{dayLabel(day)}</span>
            <span>{formatMoney(dayTotal(dayEntries, base, filters.bucket))}</span>
          </h3>
          <ul className="entries">
            {dayEntries.map((entry) => (
              <li key={entry.id}>
                <div className="entry-head">
                  <span className="title">
                    {/* The merchant's emoji, and only when the headline IS the
                        merchant — on a fallback headline (an old entry with no
                        merchant) there is nothing for it to label. Inline
                        rather than in a fixed gutter: a badge on every row of a
                        dense list adds weight without adding information, and
                        rows that have one simply read a character wider. */}
                    {entry.merchantIcon && entry.merchantName && (
                      <span className="entry-icon" aria-hidden="true">
                        {entry.merchantIcon}
                      </span>
                    )}
                    {headline(entry)}
                    {entry.isPrivate && <span className="tag">private</span>}
                    {entry.source === 'OCR' && <span className="tag">scanned</span>}
                    {entry.type === 'INCOME' && <span className="tag tag-income">income</span>}
                  </span>
                  <span className="entry-right">
                    {/* A sign, so a row that adds to the month and a row that
                        takes from it are told apart at a glance rather than by
                        reading the tag beside the title. */}
                    <span
                      className={entry.type === 'INCOME' ? 'amount amount-in' : 'amount amount-out'}
                    >
                      {entry.type === 'INCOME' ? '+' : '\u2212'}
                      {entry.amount} {base}
                    </span>
                    <span className="entry-actions">
                      <button
                        type="button"
                        className="icon-action"
                        onClick={() => onEdit(entry)}
                        aria-label={`Edit ${entry.merchantName ?? 'entry'}`}
                        title="Edit"
                      >
                        <PencilIcon />
                      </button>
                      <button
                        type="button"
                        className="icon-action icon-danger"
                        onClick={() => onDelete(entry)}
                        aria-label={`Delete ${entry.merchantName ?? 'entry'}`}
                        title="Delete"
                      >
                        <TrashIcon />
                      </button>
                    </span>
                  </span>
                </div>
                <div className="entry-meta">
                  {entry.spentByName}
                  {entry.paymentMeansName && ` · ${entry.paymentMeansName}`}
                  {itemCount(entry) > 0 && (
                    <>
                      {' · '}
                      {itemCount(entry)} item{itemCount(entry) === 1 ? '' : 's'}
                    </>
                  )}
                </div>

                {/* What was actually in the basket. Shown inline rather than
                    behind a tap, because "where did €84 at the supermarket go"
                    is the question the journal exists to answer. */}
                {itemCount(entry) > 0 && (
                  <ul className="entry-items">
                    {entry.splits.flatMap((split) =>
                      split.items.map((item) => (
                        <li key={item.id}>
                          <span className="entry-item-name">
                            {item.productName ?? item.rawText ?? 'Item'}
                            {item.quantity !== '1' && <em> ×{item.quantity}</em>}
                          </span>
                          <span className="entry-item-total">{item.lineTotal}</span>
                        </li>
                      )),
                    )}
                  </ul>
                )}
                {/* No category rows. A receipt spanning two categories used to
                    print them underneath its items, in the same block and the
                    same shape — so "Supermarket 41.10" sat under
                    "Kaffeebohnen 18.99" and the eye read one list where there
                    were two. The entry says WHERE (the merchant) and WHAT (the
                    items); which bucket each line feeds is the Budget screen's
                    question, and it answers it without this. */}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </section>
  );
}

/**
 * Everything about an entry that someone might type to find it again: who it
 * was with, what was written on it, what was in it, and the amount — because
 * "54.90" is how you find the entry you remember by its number and nothing
 * else. Built per call rather than memoised: a month of entries is a few
 * hundred short strings, and a cache that can go stale is the more expensive
 * of the two.
 */
function haystack(entry: Entry): string {
  const parts: string[] = [entry.amount];
  if (entry.merchantName) parts.push(entry.merchantName);
  if (entry.note) parts.push(entry.note);
  for (const split of entry.splits) {
    if (split.categoryName) parts.push(split.categoryName);
    for (const item of split.items) {
      if (item.productName) parts.push(item.productName);
      if (item.rawText) parts.push(item.rawText);
    }
  }
  return parts.join(' ').toLowerCase();
}

/**
 * What is left this month, as text. Lives here so the body's headline and the
 * header's small copy of it can never print different numbers.
 */
export function remainingFigure(summary: PeriodSummary | null, base: string): string {
  const remaining = summary?.unallocated ?? null;
  return remaining ? `${remaining.replace('-', '')} ${base}` : '—';
}

/** Negative `unallocated` means the month spent more than it earned. */
export function isOverspent(summary: PeriodSummary | null): boolean {
  return summary?.unallocated?.startsWith('-') ?? false;
}

// ---------------------------------------------------------------------------
// The filter half, rendered by App in the page header.
// ---------------------------------------------------------------------------

interface FilterProps {
  filters: JournalFilters;
  onFiltersChange: (next: JournalFilters) => void;
  q: string;
  onQChange: (next: string) => void;
  members: Member[];
  means: PaymentMeans[];
  /** What is left this month, shown once the body's headline scrolls away. */
  figure: string;
  showFigure: boolean;
  overspent: boolean;
}

export function EntryFilters({
  filters,
  onFiltersChange,
  q,
  onQChange,
  members,
  means,
  figure,
  showFigure,
  overspent,
}: FilterProps) {
  const [open, setOpen] = useState(false);
  const anchor = useRef<HTMLDivElement | null>(null);

  const facets = [filters.bucket, filters.spentBy, filters.paymentMeansId].filter(Boolean).length;
  const personName = members.find((m) => m.id === filters.spentBy)?.displayName ?? '';
  const meansName = means.find((m) => m.id === filters.paymentMeansId)?.name ?? '';
  const narrowed = facets > 0 || q.trim().length > 0;

  // A popover that only closes by clicking its own button is a panel with
  // extra steps. Listeners exist only while it is open, so the common case
  // costs nothing.
  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: PointerEvent) {
      if (!anchor.current?.contains(event.target as Node)) setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  function set(patch: Partial<JournalFilters>) {
    onFiltersChange({ ...filters, ...patch });
  }

  return (
    <div className="filters">
      <input
        className="journal-search"
        type="search"
        value={q}
        onChange={(e) => onQChange(e.target.value)}
        placeholder="Search this month"
        aria-label="Search this month"
      />
      {/* What is actually on, next to the controls that set it. Shown only
          when something is on, so the resting header carries no chrome
          announcing that nothing is filtered. */}
      {narrowed && (
        <>
          {filters.bucket && (
            <FilterChip
              label={BUCKET_LABELS[filters.bucket as SpendingBucket]}
              onClear={() => set({ bucket: '' })}
            />
          )}
          {filters.spentBy && (
            <FilterChip label={personName} onClear={() => set({ spentBy: '' })} />
          )}
          {filters.paymentMeansId && (
            <FilterChip label={meansName} onClear={() => set({ paymentMeansId: '' })} />
          )}
          {q.trim() && <FilterChip label={`“${q.trim()}”`} onClear={() => onQChange('')} />}
          <button
            type="button"
            className="chip chip-ghost"
            onClick={() => {
              onQChange('');
              set({ spentBy: '', paymentMeansId: '', bucket: '' });
            }}
          >
            Clear all
          </button>
        </>
      )}

      {/* The popover is positioned against the row, not this button, so it can
          span the full width — but it stays a DOM child of the anchor, because
          the click-outside test walks the DOM and not the painted box. */}
      <div className="filter-anchor" ref={anchor}>
        <button
          type="button"
          className={open || facets > 0 ? 'chip chip-on' : 'chip'}
          onClick={() => setOpen((current) => !current)}
          aria-expanded={open}
          aria-haspopup="dialog"
        >
          <FilterIcon />
          Filter{facets > 0 ? ` · ${facets}` : ''}
        </button>

        {open && (
          <div className="filter-pop" role="dialog" aria-label="Filter entries">
            <label>
              <span>Bucket</span>
              <select value={filters.bucket} onChange={(e) => set({ bucket: e.target.value })}>
                <option value="">Every bucket</option>
                {SPENDING_BUCKETS.map((bucket) => (
                  <option key={bucket} value={bucket}>
                    {BUCKET_LABELS[bucket]}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>Who</span>
              <select value={filters.spentBy} onChange={(e) => set({ spentBy: e.target.value })}>
                <option value="">Everyone</option>
                {members.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.displayName}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>Paid with</span>
              <select
                value={filters.paymentMeansId}
                onChange={(e) => set({ paymentMeansId: e.target.value })}
              >
                <option value="">Any method</option>
                {means.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name}
                  </option>
                ))}
              </select>
            </label>
          </div>
        )}
      </div>

      {/* The month's figure, taking the header's spare width once its full
          version has scrolled behind the header. It animates in rather than
          appearing, so a number arriving next to the search reads as the same
          number moving and not as a new one. */}
      <strong
        className={[
          'remaining-mini',
          showFigure ? 'remaining-mini-on' : '',
          overspent ? 'remaining-mini-over' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        aria-hidden={!showFigure}
      >
        {figure}
      </strong>
    </div>
  );
}

/** An active filter, and the way to switch it off, in one control. */
function FilterChip({ label, onClear }: { label: string; onClear: () => void }) {
  return (
    <button type="button" className="chip chip-on filter-chip" onClick={onClear}>
      {label}
      <span aria-hidden="true">×</span>
      <span className="sr-only">Remove this filter</span>
    </button>
  );
}

/* Stroke icon on a 24px grid; inherits currentColor so both themes work. */
function FilterIcon() {
  return (
    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor"
         strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 6h16" />
      <path d="M7 12h10" />
      <path d="M10 18h4" />
    </svg>
  );
}

/* Stroke icons on a 24px grid; they inherit currentColor so both themes work. */
function PencilIcon() {
  return (
    <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor"
         strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor"
         strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M3 6h18" />
      <path d="M8 6V4h8v2" />
      <path d="M19 6l-1 14H6L5 6" />
      <path d="M10 11v6M14 11v6" />
    </svg>
  );
}

/**
 * A day's spending, honouring the bucket filter.
 *
 * Filtering by Needs returns whole entries that contain a Needs split — one
 * supermarket trip really is part Needs and part Wants, and hiding the rest of
 * it would be lying about the receipt. But the day's total should then mean
 * "spent on Needs that day", not "cost of the receipts that touched Needs".
 */
function dayTotal(entries: Entry[], base: string, bucket: string): Money {
  let total = zero(base);
  for (const entry of entries) {
    if (entry.type !== 'EXPENSE') continue;
    for (const split of entry.splits) {
      // Income has no share of income; an expense filed there is a data error,
      // not something to fold into a spending total.
      if (split.bucket === 'INCOME') continue;
      if (bucket && split.bucket !== bucket) continue;
      total = add(total, parseMoney(split.amount, base));
    }
  }
  return total;
}
